import React, { useId, useRef, useState } from "react";
import { ChevronsUpDown, Lock } from "lucide-react";
import { FieldConfig, useField } from "formik";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/ui/status-badge";
import { cn } from "@/lib/utils";
import type { IconType } from "@/components/icons/legacy";
import {
  CUSTOM_TYPE_GROUP_TITLE,
  customTypeLabel,
  customTypeValue,
  customWorkflowEnabled
} from "@/features/customCaseTypes/customCaseTypes";
import type { CustomCaseType } from "@/features/customCaseTypes/types";
import {
  RFE_VISA_CATEGORY_ID,
  VISA_CATEGORIES,
  type VisaOption,
  withoutLoneVersionTags
} from "./visaOptions";
import { useEntitlements } from "../../hooks/useEntitlements";
import { useContactSales } from "../../hooks/useContactSales";
import { Feature as PlanFeature } from "../../types/entitlements";

export {
  RFE_VISA_CATEGORY_ID,
  VISA_CATEGORIES,
  type VisaOption
} from "./visaOptions";

/** Kept for callers; the picker draws its own field now. */
type LegacySelectVariant = "outline" | "filled" | "flushed" | "unstyled";

interface Props extends FieldConfig {
  label: string;
  isRequired?: boolean;
  placeholder?: string;
  isLoaded?: boolean;
  excludeCategories?: string[];
  excludeVisas?: string[]; // e.g., ["RFE"]
  selectedLabelFontSize?: string;
  /** `"unstyled"` draws the transparent-underline typeform field. */
  variant?: LegacySelectVariant;
  /** Stretch to the parent's width instead of capping at 400px. */
  isFullWidth?: boolean;
  /** Keep the label for screen readers only, e.g. when a heading already asks the question. */
  hideLabel?: boolean;
  /** The firm's saved custom case types, offered as "Custom: <name>". */
  customTypes?: readonly CustomCaseType[];
}

interface StandaloneProps {
  label: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  isRequired?: boolean;
  placeholder?: string;
  isLoaded?: boolean;
  isDisabled?: boolean;
  errorMessage?: string;
  excludeCategories?: string[]; // e.g., ["other", "humanitarian"]
  excludeVisas?: string[]; // e.g., ["RFE"]
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  /**
   * When true, renders only the picker (no label or error), with no border of
   * its own, for embedding inside another field.
   */
  bare?: boolean;
}

// New version = accent (default), Deprecated version = neutral.
const VisaOptionTag = ({ children }: { children: React.ReactNode }) => {
  const label = typeof children === "string" ? children : "";
  return (
    <StatusBadge tone={label === "Deprecated version" ? "neutral" : "accent"}>
      {children}
    </StatusBadge>
  );
};

// Render visa option label with optional tag (used for the dropdown menu rows).
export const renderVisaLabel = (visa: VisaOption) => (
  <span className="flex w-full items-center justify-between gap-2">
    <span className="truncate">{visa.label}</span>
    {visa.tag && <VisaOptionTag>{visa.tag}</VisaOptionTag>}
  </span>
);

// Render the label for the *selected* (closed) state. Uses a compact inline
// layout (no full-width space-between) so the tag doesn't overflow the
// trigger, and keeps the tag so version-tagged entries that share a label
// (e.g. both "EB-2 NIW" variants) remain distinguishable.
export const renderSelectedLabel = (
  rawLabel: string,
  tag?: string,
  fontSize?: string
) => (
  <span className="flex min-w-0 items-center gap-2">
    <span
      className="truncate text-inherit"
      style={fontSize ? { fontSize, fontWeight: 400 } : undefined}
    >
      {rawLabel}
    </span>
    {tag && <VisaOptionTag>{tag}</VisaOptionTag>}
  </span>
);

type GroupLabelData = { label: string; icon?: IconType };

// Format group labels with icons
export const formatGroupLabel = (data: GroupLabelData) => {
  const GroupIcon = data.icon;
  return (
    <span className="flex items-center gap-2">
      {GroupIcon && <GroupIcon className="size-3.5 shrink-0 text-fg-3" />}
      <span className="text-sm font-medium text-fg-2">{data.label}</span>
    </span>
  );
};

export type VisaCategoryLock = {
  /** Categories shown but not selectable, because the plan excludes them. */
  lockedCategoryIds: string[];
  onUpgradeClick: () => void;
};

// Locked options stay visible so the workflow reads as available-for-purchase
// rather than missing, and the badge opens a sales conversation.
export const renderLockedVisaLabel = (
  visa: VisaOption,
  onUpgradeClick: () => void
) => (
  <span className="flex w-full items-center justify-between gap-2">
    <span className="truncate text-fg-4">{visa.label}</span>
    <span
      role="button"
      tabIndex={0}
      className="inline-flex h-5 shrink-0 cursor-pointer items-center gap-1 rounded-full bg-fill-2 px-2 text-sm text-accent-seed transition-colors duration-100 hover:bg-fill-3 focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none"
      onMouseDown={(event: React.MouseEvent) => {
        // Keep the menu from swallowing the click on a disabled option.
        event.preventDefault();
        event.stopPropagation();
      }}
      onClick={(event: React.MouseEvent) => {
        event.stopPropagation();
        onUpgradeClick();
      }}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key !== "Enter" && event.key !== " ") {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        onUpgradeClick();
      }}
    >
      <Lock className="size-3 shrink-0" aria-hidden />
      Contact Sales to Upgrade
    </span>
  </span>
);

/**
 * Locks the RFE / NOID / NOIR workflows for firms whose plan excludes them.
 * Those workflows are a drafting exercise (the case agent drafts the notice
 * response), so they ride on the drafting entitlement.
 */
const useVisaCategoryLock = (): VisaCategoryLock => {
  const { hasFeature } = useEntitlements();
  const { contactSales } = useContactSales();

  return {
    lockedCategoryIds: hasFeature(PlanFeature.DRAFTING)
      ? []
      : [RFE_VISA_CATEGORY_ID],
    onUpgradeClick: contactSales
  };
};

export type VisaSelectOption = {
  label: React.ReactNode;
  value: string;
  tag?: string;
  rawLabel: string;
  isDisabled: boolean;
};

export type VisaSelectGroup = {
  label: string;
  icon: IconType | undefined;
  options: VisaSelectOption[];
};

// Convert to grouped options format
export const getGroupedOptions = (
  excludeCategories: string[] = [],
  excludeVisas: string[] = [],
  lock?: VisaCategoryLock
): VisaSelectGroup[] =>
  VISA_CATEGORIES.filter((category) => !excludeCategories.includes(category.id))
    .map((category) => {
      const isLocked = !!lock?.lockedCategoryIds.includes(category.id);

      return {
        label: category.title,
        icon: category.icon,
        options: withoutLoneVersionTags(
          category.visas.filter((visa) => !excludeVisas.includes(visa.value))
        ).map((visa) => ({
          label:
            isLocked && lock
              ? renderLockedVisaLabel(visa, lock.onUpgradeClick)
              : renderVisaLabel(visa),
          value: visa.value,
          tag: visa.tag,
          rawLabel: visa.label,
          isDisabled: isLocked
        }))
      };
    })
    .filter((category) => category.options.length > 0); // Remove empty categories

/** The firm's custom case types as one more group, where Custom Workflow is on. */
export const withCustomTypes = (
  groups: VisaSelectGroup[],
  types: readonly CustomCaseType[] = []
): VisaSelectGroup[] =>
  customWorkflowEnabled() && types.length > 0
    ? [
        ...groups,
        {
          label: CUSTOM_TYPE_GROUP_TITLE,
          icon: undefined,
          options: types.map((t) => {
            const option = {
              value: customTypeValue(t.id),
              label: customTypeLabel(t.name)
            };
            return {
              label: renderVisaLabel(option),
              value: option.value,
              rawLabel: option.label,
              isDisabled: false
            };
          })
        }
      ]
    : groups;

const findOption = (groups: VisaSelectGroup[], value: string | undefined) =>
  value
    ? groups
        .flatMap((group) => group.options)
        .find((option) => option.value === value) ?? null
    : null;

type PickerAppearance = "field" | "underline" | "bare";

type VisaComboboxProps = {
  id?: string;
  groups: VisaSelectGroup[];
  value: string | undefined;
  onSelect: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  invalid?: boolean;
  appearance: PickerAppearance;
  selectedLabelFontSize?: string;
  onOpenChange?: (open: boolean) => void;
};

const TRIGGER_CLASS: Record<PickerAppearance, string> = {
  field: "h-7 w-full min-w-75 justify-between px-2",
  underline:
    "h-12 w-full justify-between rounded-none border-b border-line-3 px-0 text-xl inset-ring-0 hover:border-line-4 hover:bg-transparent focus-visible:border-primary focus-visible:ring-0 aria-expanded:border-primary aria-expanded:bg-transparent",
  bare: "h-full w-full min-w-0 justify-between px-0 inset-ring-0 hover:bg-transparent aria-expanded:bg-transparent"
};

const VisaCombobox = ({
  id,
  groups,
  value,
  onSelect,
  placeholder,
  disabled,
  invalid,
  appearance,
  selectedLabelFontSize,
  onOpenChange
}: VisaComboboxProps) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const focusReturnedRef = useRef(false);
  // cmdk highlights the first option on open; Enter only picks once the
  // attorney has typed or moved, so a stray Enter can't choose for them.
  const choseRef = useRef(false);
  const selected = findOption(groups, value);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      focusReturnedRef.current = false;
      choseRef.current = false;
    }
    setOpen(next);
    onOpenChange?.(next);
  };

  return (
    // `modal` so the list scrolls when the picker is opened inside a Sheet or
    // Dialog: without it the scroll lock swallows wheel events over the
    // portaled list.
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          ref={triggerRef}
          id={id}
          type="button"
          variant="secondary"
          role="combobox"
          aria-expanded={open}
          aria-invalid={invalid || undefined}
          onKeyDown={(event) => {
            if (event.key !== "ArrowDown" || open) return;
            event.preventDefault();
            handleOpenChange(true);
          }}
          className={cn(
            "font-normal text-fg",
            TRIGGER_CLASS[appearance],
            invalid && appearance === "underline" && "border-danger-line"
          )}
        >
          {selected ? (
            renderSelectedLabel(
              selected.rawLabel ?? selected.value,
              selected.tag,
              selectedLabelFontSize
            )
          ) : (
            <span
              className="truncate text-fg-4"
              style={
                selectedLabelFontSize
                  ? { fontSize: selectedLabelFontSize }
                  : undefined
              }
            >
              {placeholder}
            </span>
          )}
          {appearance === "bare" ? null : (
            <ChevronsUpDown className="size-3.5 shrink-0 text-fg-3" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        onCloseAutoFocus={(event) => {
          if (focusReturnedRef.current) event.preventDefault();
        }}
        className={cn(
          "w-(--radix-popover-trigger-width) min-w-64 p-0",
          appearance !== "underline" && "max-w-100"
        )}
      >
        <Command
          onKeyDown={(event) => {
            if (event.key === "Enter" && !choseRef.current) {
              event.preventDefault();
              event.stopPropagation();
            } else if (
              event.key === "ArrowDown" ||
              event.key === "ArrowUp" ||
              event.key === "Home" ||
              event.key === "End"
            ) {
              choseRef.current = true;
            }
          }}
        >
          <CommandInput
            placeholder="Search workflows..."
            onValueChange={(search) => {
              choseRef.current = search.trim().length > 0;
            }}
          />
          <CommandList className="max-h-75">
            <CommandEmpty>No workflow found.</CommandEmpty>
            {groups.map((group) => (
              <CommandGroup key={group.label} heading={formatGroupLabel(group)}>
                {group.options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={`${option.rawLabel} ${option.value} ${
                      option.tag ?? ""
                    }`}
                    disabled={option.isDisabled}
                    data-checked={option.value === value || undefined}
                    className="data-[disabled=true]:pointer-events-auto data-[disabled=true]:opacity-100"
                    onSelect={() => {
                      handleOpenChange(false);
                      onSelect(option.value);
                      // Radix returns focus to the trigger on a deferred
                      // timer; by then the user may have moved on, and the
                      // late focus would pull them back to this field.
                      focusReturnedRef.current = true;
                      queueMicrotask(() => triggerRef.current?.focus());
                    }}
                  >
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const FieldLabel = ({
  htmlFor,
  label,
  isRequired,
  className
}: {
  htmlFor: string;
  label: string;
  isRequired?: boolean;
  className?: string;
}) => (
  <Label htmlFor={htmlFor} className={cn("gap-1 text-fg-2", className)}>
    {label}
    {isRequired ? (
      <span className="text-danger" aria-hidden>
        *
      </span>
    ) : null}
  </Label>
);

// Standalone version for non-Formik contexts
export const VisaTypeSelectorStandalone = ({
  label,
  value,
  onChange,
  isRequired = false,
  placeholder = "Select Visa Type",
  isLoaded = true,
  isDisabled = false,
  errorMessage,
  excludeCategories = [],
  excludeVisas = [],
  onMenuOpen,
  onMenuClose,
  bare = false
}: StandaloneProps) => {
  const id = useId();
  const categoryLock = useVisaCategoryLock();
  const groupedOptions = getGroupedOptions(
    excludeCategories,
    excludeVisas,
    categoryLock
  );

  const selectEl = isLoaded ? (
    <VisaCombobox
      id={id}
      groups={groupedOptions}
      value={value}
      onSelect={(next) => onChange(next || undefined)}
      placeholder={placeholder}
      disabled={isDisabled}
      invalid={!!errorMessage}
      appearance={bare ? "bare" : "field"}
      onOpenChange={(open) => (open ? onMenuOpen?.() : onMenuClose?.())}
    />
  ) : (
    <Skeleton className="h-7 w-full" />
  );

  if (bare) return <div className="w-full">{selectEl}</div>;

  return (
    <div className="flex w-full flex-col gap-1.5">
      <FieldLabel htmlFor={id} label={label} isRequired={isRequired} />
      {selectEl}
      {errorMessage && <p className="text-sm text-danger">{errorMessage}</p>}
    </div>
  );
};

// Formik version
export const VisaTypeSelector = ({
  label,
  isRequired = false,
  placeholder = "Select workflow...",
  isLoaded = true,
  excludeCategories = [],
  excludeVisas = [],
  selectedLabelFontSize,
  variant,
  isFullWidth = false,
  hideLabel = false,
  customTypes,
  ...props
}: Props) => {
  const id = useId();
  const [field, meta, helpers] = useField(props.name);
  const { setValue } = helpers;
  const categoryLock = useVisaCategoryLock();

  // Convert to grouped options format, filtering out excluded categories and visas
  const groupedOptions = withCustomTypes(
    getGroupedOptions(excludeCategories, excludeVisas, categoryLock),
    customTypes
  );

  const isInvalid = meta.touched && !!meta.error;
  const appearance: PickerAppearance =
    variant === "unstyled" ? "underline" : "field";

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-1.5",
        isFullWidth ? "max-w-full" : "max-w-100"
      )}
    >
      <FieldLabel
        htmlFor={id}
        label={label}
        isRequired={isRequired}
        className={hideLabel ? "sr-only" : undefined}
      />
      {isLoaded ? (
        <VisaCombobox
          id={id}
          groups={groupedOptions}
          value={field.value || undefined}
          onSelect={(next) => setValue(next || "")}
          placeholder={placeholder}
          invalid={isInvalid}
          appearance={appearance}
          selectedLabelFontSize={selectedLabelFontSize}
        />
      ) : (
        <Skeleton
          className={cn("w-full", appearance === "underline" ? "h-12" : "h-7")}
        />
      )}
      {isInvalid && <p className="text-sm text-danger">{meta.error}</p>}
    </div>
  );
};
